import React, { FC, ReactNode, useContext, useEffect, useId, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { SKIP_ACCOUNT_CONNECTION } from '../constants'
import { APP_PATHS } from '../routes/paths'
import AuthService from '../services/AuthService'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import useLocalStorage from '../utils/useLocalStorage'

import { setCurrentUser as setCurrentUserReducer } from './authSlice'
import { User, UserContextType, UserRolesEnum } from './types'

export const UserContext = React.createContext<UserContextType>({} as UserContextType)

export const getIsOnboadringCompleted = (user: User | null, isAccountConnected: boolean) => {
  const { phone, country, isQuestionnaireCompleted, role } = user || {}
  const isProfileCompleted = phone && country

  if (role === UserRolesEnum.ADMIN) {
    return true
  } else {
    return !!(isProfileCompleted && isQuestionnaireCompleted && isAccountConnected)
  }
}

export const UserContextComponent: FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const { userAccounts } = useAppSelector(({ userAccounts }) => userAccounts)
  const [isAccountConnected, setIsAccountConnected] = useState(false)

  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const personalDataFormId = useId()

  const isAuthentificated = currentUser !== null

  const [skipAccountConnection, setSkipAccountConnection] = useLocalStorage<boolean>(
    `${SKIP_ACCOUNT_CONNECTION}_${currentUser?.id}`,
    false,
  )

  const isOnboadringCompleted = getIsOnboadringCompleted(
    currentUser,
    isAccountConnected || skipAccountConnection,
  )

  useEffect(() => {
    if (userAccounts.length) {
      setIsAccountConnected(true)
    }
  }, [userAccounts])

  const onLogout = () => {
    AuthService.logout()
    navigate(APP_PATHS.auth)
    setCurrentUser(null)
  }

  useEffect(() => {
    dispatch(setCurrentUserReducer(currentUser))
  }, [currentUser])

  const context = {
    isAuthentificated,
    currentUser,
    setCurrentUser,
    personalDataFormId,
    isOnboadringCompleted,
    setIsAccountConnected,
    setSkipAccountConnection,
    isAccountConnected,
    onLogout,
  }

  return <UserContext.Provider value={context}>{children}</UserContext.Provider>
}

export const useUser = () => {
  const userContext = useContext(UserContext)

  return userContext
}
